import { WarningOutlined } from '@mui/icons-material';
import { Button, Tooltip } from '@mui/material';
import { MainLayout as CoreMainLayout, IMainLayoutProps } from '@myhrbox/myhrbox-frontend-core';
import ROUTES, { RoutesValues, isHeaderlessPublicRoute } from 'constants/routes';
import { useDialog } from 'context/dialogContext';
import { useUnit } from 'effector-react';
import withErrorBoundary from 'hocs/withErrorBoundary';
import useNavigateWithReplace from 'hooks/useNavigateReplace';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { $isPublicExamination } from 'store/assessment/model';
import useMe from 'store/user/useMe';
import { getIsNeedToGoToConfirmUserData } from 'utils/user';
import BugReportForm from './components/BugReportForm';
import Header from './components/Header';
import HeaderlessMainLayout from './components/HeaderlessMainLayout';

interface MainLayoutProps extends IMainLayoutProps {
  hideHeader?: boolean;
  skipMe?: boolean;
}

const MainLayout = (props: MainLayoutProps) => {
  const { navigate } = useNavigateWithReplace();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { children, hideHeader = false, skipMe = false, onLogoClick, ...rest } = props;
  const { me, loading: meLoading } = useMe(skipMe);
  const isPublicExamination = useUnit($isPublicExamination);
  const { showDialog, closeDialog } = useDialog();

  const pathname = location.pathname as RoutesValues;
  const shouldHideHeader = hideHeader || isHeaderlessPublicRoute(pathname);

  useEffect(() => {
    if (skipMe) {
      return;
    }

    if (me) {
      if (me.language && i18n.language !== me.language) {
        i18n.changeLanguage(me.language);
      }

      if (
        !isPublicExamination &&
        ![ROUTES.CONFIRM_REGISTER, ROUTES.EXAMINATION_RECEIVE].some((route) =>
          pathname.includes(route),
        )
      ) {
        if (!me.language) {
          if (!pathname.includes(ROUTES.LANGUAGE_SELECT)) navigate(ROUTES.LANGUAGE_SELECT);
        } else if (getIsNeedToGoToConfirmUserData(me)) {
          if (!pathname.includes(ROUTES.CONFIRM_USER_DATA)) {
            navigate(ROUTES.CONFIRM_USER_DATA);
          }
        } else if (
          pathname.includes(ROUTES.LANGUAGE_SELECT) ||
          pathname.includes(ROUTES.CONFIRM_USER_DATA) ||
          pathname.includes(ROUTES.LOGIN)
        ) {
          navigate(ROUTES.ASSESSMENT);
        }
      }
    }
  }, [i18n, isPublicExamination, me, navigate, pathname, skipMe]);

  const openBugReportModal = () => {
    showDialog({
      title: t('assessment.bugReport.title'),
      content: <BugReportForm onCancel={closeDialog} />,
    });
  };

  if (shouldHideHeader) {
    return <HeaderlessMainLayout {...rest}>{children}</HeaderlessMainLayout>;
  }

  return (
    <CoreMainLayout
      onLogoClick={() => navigate(ROUTES.ASSESSMENT)}
      HeaderComponent={me ? <Header me={me} meLoading={meLoading} /> : undefined}
      {...rest} // eslint-disable-line react/jsx-props-no-spreading
    >
      {children}
      <Tooltip title={t('assessment.bugReport.title')}>
        <Button
          variant='text'
          sx={{ position: 'fixed', right: 5, bottom: 15 }}
          onClick={openBugReportModal}>
          <WarningOutlined sx={(theme) => ({ color: theme.palette.logo, width: 28, height: 28 })} />
        </Button>
      </Tooltip>
    </CoreMainLayout>
  );
};

export default withErrorBoundary(MainLayout);
